import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Breadcrumbs, Link, Stack, Typography, type SxProps, type Theme } from '@mui/material';
import { tokens } from '../theme';

/** Centered content column used by every section (1240px max, 32px gutters on desktop). */
export function Section({
  children,
  band,
  py = { xs: 6, md: 12 },
  sx,
  id,
}: {
  children: ReactNode;
  /** Full-width background behind the column. */
  band?: 'subtle' | 'tint' | 'brand';
  py?: number | Record<string, number>;
  sx?: SxProps<Theme>;
  id?: string;
}) {
  const bg =
    band === 'subtle' ? tokens.subtle : band === 'tint' ? tokens.tint : band === 'brand' ? tokens.blue : undefined;
  return (
    <Box
      component="section"
      id={id}
      sx={{
        bgcolor: bg,
        color: band === 'brand' ? '#fff' : undefined,
        borderTop: band === 'subtle' ? `1px solid ${tokens.divider}` : undefined,
        borderBottom: band === 'subtle' ? `1px solid ${tokens.divider}` : undefined,
      }}
    >
      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, sm: 4 }, py, ...sx }}>{children}</Box>
    </Box>
  );
}

/** Grey title band with breadcrumb at the top of inner pages. */
export function PageHeader({
  title,
  subtitle,
  crumbs = [],
  aside,
}: {
  title: string;
  subtitle?: ReactNode;
  crumbs?: Array<{ label: string; to?: string }>;
  aside?: ReactNode;
}) {
  return (
    <Section band="subtle" py={{ xs: 5, md: 7 }} sx={{ borderTop: 0 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={4} alignItems={{ md: 'flex-end' }} justifyContent="space-between">
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Breadcrumbs aria-label="Breadcrumb" sx={{ fontSize: 14, color: 'text.secondary' }}>
            <Link component={RouterLink} to="/" fontWeight={500}>
              Home
            </Link>
            {crumbs.map((c) =>
              c.to ? (
                <Link key={c.label} component={RouterLink} to={c.to} fontWeight={500}>
                  {c.label}
                </Link>
              ) : null,
            )}
            <Typography fontSize={14} color="text.secondary">
              {title}
            </Typography>
          </Breadcrumbs>
          <Typography variant="h1" sx={{ fontSize: { xs: 36, md: 52 } }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: { xs: 18, md: 20 }, color: 'text.secondary', maxWidth: 640 }}>
              {subtitle}
            </Typography>
          )}
        </Stack>
        {aside}
      </Stack>
    </Section>
  );
}

/** Square-ish monogram tile used in place of product imagery. */
export function AppMonogram({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'hero' }) {
  const words = name.split(/\s+/).filter((w) => /[A-Za-z]/.test(w[0] ?? ''));
  const initials = (
    words.length > 1 ? words.slice(-2).map((w) => w[0]).join('') : (words[0] ?? '?').slice(0, 2)
  ).toUpperCase();
  if (size === 'sm') {
    return (
      <Box
        aria-hidden
        sx={{
          width: 40,
          height: 40,
          flex: 'none',
          borderRadius: 1.5,
          bgcolor: 'primary.main',
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          fontWeight: 700,
          fontSize: 14,
        }}
      >
        {initials}
      </Box>
    );
  }
  return (
    <Box
      aria-hidden
      sx={{
        aspectRatio: size === 'hero' ? '16 / 9' : '16 / 10',
        borderRadius: 2,
        bgcolor: tokens.tint,
        color: 'primary.main',
        display: 'grid',
        placeItems: 'center',
        fontWeight: 800,
        letterSpacing: '-0.03em',
        fontSize: size === 'hero' ? 56 : 40,
      }}
    >
      {initials}
    </Box>
  );
}
